import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import { assertAdmin } from "@/lib/admin-guard.server";
import { cached, invalidateCached } from "@/lib/server-cache";
import type { InstagramComment, InstagramHighlight, InstagramPost } from "@/lib/instagram";

function publicClient() {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return createClient<Database>(process.env["SUPABASE_URL"]!, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

type StoredChild = {
  id: string;
  mediaType: string;
  mediaUrl: string;
  thumbnailUrl: string;
  permalink: string;
  storedPath?: string | null;
  storedThumbPath?: string | null;
};

type PostRow = {
  id: string;
  media_id: string;
  media_type: string;
  is_reel: boolean;
  caption: string;
  media_url: string;
  thumbnail_url: string;
  permalink: string;
  posted_at: string | null;
  hidden: boolean;
  stored_path?: string | null;
  stored_thumb_path?: string | null;
  username?: string | null;
  like_count?: number | null;
  comments_count?: number | null;
  children?: StoredChild[] | null;
  comments?: InstagramComment[] | null;
};

/** Egen, permanent adress till en sparad Instagram-fil. */
function storedUrl(path?: string | null): string {
  return path ? `/api/public/instagrambild/${path}` : "";
}

function mapPost(row: PostRow): InstagramPost {
  return {
    id: row.id,
    mediaId: row.media_id,
    mediaType: row.media_type,
    isReel: row.is_reel,
    caption: row.caption,
    mediaUrl: storedUrl(row.stored_path) || row.media_url,
    thumbnailUrl: storedUrl(row.stored_thumb_path) || row.thumbnail_url,
    videoUrl: row.media_url,
    permalink: row.permalink,
    postedAt: row.posted_at,
    hidden: row.hidden,
    username: row.username ?? undefined,
    likeCount: row.like_count ?? undefined,
    commentsCount: row.comments_count ?? undefined,
    carousel: (row.children ?? []).map((child) => ({
      id: child.id,
      mediaType: child.mediaType,
      mediaUrl: storedUrl(child.storedPath) || child.mediaUrl,
      thumbnailUrl: storedUrl(child.storedThumbPath) || child.thumbnailUrl || child.mediaUrl,
      permalink: child.permalink,
    })),
    comments: row.comments ?? [],
  };
}

const POST_COLUMNS =
  "id, media_id, media_type, is_reel, caption, media_url, thumbnail_url, permalink, posted_at, hidden, stored_path, stored_thumb_path, username, like_count, comments_count, children, comments";


/** Publik läsning av synliga inlägg och teman. */
export const getInstagramFeed = createServerFn({ method: "GET" }).handler(async () =>
  cached("instagram-feed", 120_000, async () => {
  const supabase = publicClient();

  const [postsRes, highlightsRes, linksRes] = await Promise.all([
    supabase
      .from("instagram_posts")
      .select(POST_COLUMNS)
      .eq("hidden", false)
      .order("posted_at", { ascending: false, nullsFirst: false })
      .limit(60),
    supabase
      .from("instagram_highlights")
      .select("id, title_sv, title_en, title_es, description_sv, description_en, description_es, sort_order, visible")
      .eq("visible", true)
      .order("sort_order", { ascending: true }),
    supabase
      .from("instagram_highlight_posts")
      .select("highlight_id, post_id, sort_order")
      .order("sort_order", { ascending: true }),
  ]);

  const posts = (postsRes.data ?? []).map((row) => mapPost(row as PostRow));
  const links = linksRes.data ?? [];

  const highlights: InstagramHighlight[] = (highlightsRes.data ?? []).map((row) => ({
    id: row.id,
    titleSv: row.title_sv,
    titleEn: row.title_en,
    titleEs: row.title_es ?? "",
    descriptionSv: row.description_sv,
    descriptionEn: row.description_en,
    descriptionEs: row.description_es ?? "",
    sortOrder: row.sort_order,
    visible: row.visible,
    postIds: links.filter((l) => l.highlight_id === row.id).map((l) => l.post_id),
  }));

  return { posts, highlights };
  }),
);

/** Admin: alla inlägg inklusive dolda, teman och synkstatus. */
export const getInstagramAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as never, "/admin/instagram");

    const [postsRes, highlightsRes, linksRes] = await Promise.all([
      context.supabase
        .from("instagram_posts")
        .select(POST_COLUMNS)
        .order("posted_at", { ascending: false, nullsFirst: false })
        .limit(120),
      context.supabase
        .from("instagram_highlights")
        .select("id, title_sv, title_en, title_es, description_sv, description_en, description_es, sort_order, visible")
        .order("sort_order", { ascending: true }),
      context.supabase
        .from("instagram_highlight_posts")
        .select("highlight_id, post_id, sort_order"),
    ]);

    const links = linksRes.data ?? [];
    const { readSyncStatus } = await import("@/lib/instagram.server");

    return {
      posts: (postsRes.data ?? []).map((row) => mapPost(row as PostRow)),
      highlights: (highlightsRes.data ?? []).map((row) => ({
        id: row.id,
        titleSv: row.title_sv,
        titleEn: row.title_en,
        titleEs: row.title_es ?? "",
        descriptionSv: row.description_sv,
        descriptionEn: row.description_en,
        descriptionEs: row.description_es ?? "",
        sortOrder: row.sort_order,
        visible: row.visible,
        postIds: links.filter((l) => l.highlight_id === row.id).map((l) => l.post_id),
      })) satisfies InstagramHighlight[],
      status: await readSyncStatus(),
    };
  });

/** Admin: hämta senaste inläggen från Instagram direkt. */
export const syncInstagramNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as never, "/admin/instagram");
    const { syncInstagram } = await import("@/lib/instagram.server");
    const res = await syncInstagram();
    invalidateCached("instagram-feed");
    return res;
  });

/** Admin: visa eller dölj ett inlägg. */
export const setInstagramPostHidden = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string; hidden: boolean }) =>
    z.object({ id: z.string().uuid(), hidden: z.boolean() }).parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context as never, "/admin/instagram");
    const { error } = await context.supabase
      .from("instagram_posts")
      .update({ hidden: data.hidden })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    invalidateCached("instagram-feed");
    return { ok: true };
  });

const highlightSchema = z.object({
  id: z.string().uuid().optional(),
  titleSv: z.string().max(120),
  titleEn: z.string().max(120),
  titleEs: z.string().max(120).default(""),
  descriptionSv: z.string().max(600),
  descriptionEn: z.string().max(600),
  descriptionEs: z.string().max(600).default(""),
  sortOrder: z.number().int().min(0).max(999),
  visible: z.boolean(),
  postIds: z.array(z.string().uuid()).max(30),
});

/** Admin: skapa eller uppdatera ett tema med tillhörande inlägg. */
export const saveInstagramHighlight = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => highlightSchema.parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context as never, "/admin/instagram");

    const row = {
      title_sv: data.titleSv,
      title_en: data.titleEn,
      title_es: data.titleEs,
      description_sv: data.descriptionSv,
      description_en: data.descriptionEn,
      description_es: data.descriptionEs,
      sort_order: data.sortOrder,
      visible: data.visible,
    };

    let highlightId = data.id;
    if (highlightId) {
      const { error } = await context.supabase
        .from("instagram_highlights")
        .update(row)
        .eq("id", highlightId);
      if (error) throw new Error(error.message);
    } else {
      const { data: created, error } = await context.supabase
        .from("instagram_highlights")
        .insert(row)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      highlightId = created.id;
    }

    await context.supabase.from("instagram_highlight_posts").delete().eq("highlight_id", highlightId);
    if (data.postIds.length > 0) {
      const { error } = await context.supabase.from("instagram_highlight_posts").insert(
        data.postIds.map((postId, index) => ({
          highlight_id: highlightId!,
          post_id: postId,
          sort_order: index,
        })),
      );
      if (error) throw new Error(error.message);
    }

    return { id: highlightId };
  });

/** Admin: ta bort ett tema. */
export const deleteInstagramHighlight = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context as never, "/admin/instagram");
    const { error } = await context.supabase
      .from("instagram_highlights")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    invalidateCached("instagram-feed");
    return { ok: true };
  });
